import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Row, Col, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import api from '../../api';
import PaginationComponent from '../../components/PaginationComponent';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import VotacaoViewModel from '../../viewmodel/VotacaoViewModel';
import CandidatoViewModel from '../../viewmodel/CandidatoViewModel';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import ConfirmarAusenciaModal from './ConfirmarAusenciaModal';
import VotacaoModal from './VotacaoModal';
import ConfirmarInicioModal from './ConfirmarInicioModal';
import { Voto, VotoCandidato } from '../../model/banca/votacao/Voto';
import MembroBanca, { PapelNome, StatusMembroNome } from '../../model/banca/MembroBanca';
import { VinculoNome } from '../../model/Pessoa';
import { TipoBancaNome } from '../../model/banca/Banca';
import { TipoBancaViewModel } from '../../viewmodel/banca/TipoBancaViewModel';
import { BsFillPatchCheckFill, BsFillQuestionCircleFill, BsBarChart, BsPeople, BsCheckCircle, BsBarChartFill } from 'react-icons/bs';
import './VotacaoPresidente.css';
import FinalizarBancaModal from './FinalizarBancaModal';
import TutorialVotacaoPresidente from './TutorialVotacaoPresidente';
import TooltipWrapper from '../../components/TooltipWrapper';
import MidiaModal from '../../components/MidiaModal';
import AutodeclaracaoRacialBadge from '../../components/AutodeclaracaoRacialBadge';
import { getConfigBanca } from '../../utils/tipoBancaConfig';

interface InscricaoEdital {
    editalNome: string;
    inscricao: string;
}

interface CandidatoBancaBasico {
    id?: string;
    nome: string;
    nomeSocial?: string;
    inscricao: string;
    editais?: InscricaoEdital[];
    resultado?: string;
    autodeclaracaoRacial?: string;
}

interface PageResponse<T> {
    content: T[];
    pageable: {
        pageNumber: number;
        pageSize: number;
        offset: number;
    };
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    numberOfElements: number;
}

interface VotoInfo {
    id: string;
    voto: string;
    justificativa?: string;
    dataHoraVotacao: string;
}

interface EstatisticasVotacao {
    votosRealizados: number;
    naoIniciados: number;
    fechados: number;
    ausentes: number;
    validados: number;
    naoValidados: number;
}

interface VotacaoPresidenteResponse {
    naoIniciada: {
        candidatos: PageResponse<CandidatoBancaBasico>;
    };
    aberta?: {
        candidato: CandidatoBancaBasico;
    };
    fechada: {
        candidatos: PageResponse<CandidatoBancaBasico>;
    };
    voto?: VotoInfo;
    estatisticas: EstatisticasVotacao;
}

const VotacaoPresidente: React.FC = () => {
    const { bancaId } = useParams<{ bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [dados, setDados] = useState<VotacaoPresidenteResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [paginaNaoIniciada, setPaginaNaoIniciada] = useState(0);
    const [paginaFechada, setPaginaFechada] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmarAusenciaModal, setShowConfirmarAusenciaModal] = useState(false);
    const [candidatoSelecionado, setCandidatoSelecionado] = useState<{ id: string; nome: string } | null>(null);
    const [showVotacaoModal, setShowVotacaoModal] = useState(false);
    const [isVotando, setIsVotando] = useState(false);
    const [isAtualizando, setIsAtualizando] = useState(false);
    const [tipoBanca, setTipoBanca] = useState<TipoBancaNome | null>(null);
    const [showMidiaModal, setShowMidiaModal] = useState(false);
    const [midiaUrl, setMidiaUrl] = useState<string | null>(null);
    const [tipoMidia, setTipoMidia] = useState<'video' | 'image'>('video');
    const [tituloMidiaModal, setTituloMidiaModal] = useState('Mídia do Candidato');
    const [fotoDisponivelRecursal, setFotoDisponivelRecursal] = useState(false);

    const configBanca = useMemo((): Partial<ReturnType<typeof getConfigBanca>> => 
        tipoBanca ? getConfigBanca(tipoBanca) : {}, 
        [tipoBanca]
    );
    const isBancaRecursal = tipoBanca === TipoBancaNome.RECURSAL;
    const [showFinalizarBancaModal, setShowFinalizarBancaModal] = useState(false);
    const [bancaFinalizada, setBancaFinalizada] = useState(false);
    const [showConfirmarInicioModal, setShowConfirmarInicioModal] = useState(false);
    const [candidatoParaIniciar, setCandidatoParaIniciar] = useState<{ id: string; nome: string } | null>(null);
    const [isTutorialMode, setIsTutorialMode] = useState(false);
    const [tutorialOpacities, setTutorialOpacities] = useState({ naoIniciado: 1, andamento: 1, finalizado: 1 });
    const [showTutorialModal, setShowTutorialModal] = useState(false);
    const [tutorialVotosRealizados, setTutorialVotosRealizados] = useState(0);
    const [highlightedButton, setHighlightedButton] = useState<string | null>(null);
    const [isStartingTutorial, setIsStartingTutorial] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [showStatistics, setShowStatistics] = useState(false);

    const resetTutorialState = () => {
        setIsTutorialMode(false);
        setTutorialOpacities({ naoIniciado: 1, andamento: 1, finalizado: 1 });
        setShowTutorialModal(false);
        setTutorialVotosRealizados(0);
        setHighlightedButton(null);
        setIsStartingTutorial(false);
        setIsFadingOut(false);
        carregarDados(); // Recarregar dados reais
    };


    useEffect(() => {
        setBreadcrumbs([
            { path: '/', breadcrumb: '🏠' },
            { path: `/bancas/${bancaId}/votacao-presidente`, breadcrumb: 'Visão do Presidente' },
        ]);
    }, [bancaId, setBreadcrumbs]);

    const carregarTipoBanca = async () => {
        if (!bancaId) return;
        try {
            const tipo = await TipoBancaViewModel.getTipoBanca(bancaId);
            setTipoBanca(tipo.nome);
        } catch (error) {
            console.error('Erro ao carregar tipo da banca:', error);
        }
    };

    useEffect(() => {
        carregarTipoBanca();
    }, [bancaId]);

    const getMockTutorialData = () => {
        return {
            naoIniciada: {
                candidatos: {
                    content: [{
                        id: 'mock-1',
                        nome: 'João Silva Santos',
                        inscricao: '2024001'
                    }],
                    totalElements: 1,
                    totalPages: 1,
                    pageable: { pageNumber: 0, pageSize: 8, offset: 0 },
                    first: true,
                    last: true,
                    numberOfElements: 1
                }
            },
            aberta: {
                candidato: {
                    id: 'mock-2',
                    nome: 'Maria Oliveira Costa',
                    inscricao: '2024002'
                }
            },
            fechada: {
                candidatos: {
                    content: [{
                        id: 'mock-3',
                        nome: 'Carlos Eduardo Lima',
                        inscricao: '2024003',
                        resultado: 'VALIDADO'
                    }],
                    totalElements: 1,
                    totalPages: 1,
                    pageable: { pageNumber: 0, pageSize: 8, offset: 0 },
                    first: true,
                    last: true,
                    numberOfElements: 1
                }
            },
            voto: {
                id: 'mock-voto',
                voto: 'VALIDADO',
                justificativa: 'Candidato atende aos critérios estabelecidos',
                dataHoraVotacao: new Date().toISOString()
            },
            estatisticas: {
                votosRealizados: tutorialVotosRealizados,
                naoIniciados: 1,
                fechados: 1,
                ausentes: 0,
                validados: 1,
                naoValidados: 0
            }
        };
    };

    const carregarDados = async (silencioso = false) => {
        if (!bancaId) return;

        if (isTutorialMode) {
            setDados(getMockTutorialData());
            setLoading(false);
            return;
        }

        if (!silencioso) {
            setLoading(true);
        } else {
            setIsAtualizando(true);
        }
        try {
            const pageSize = 8;
            const response = await api.get(`/bancas/${bancaId}/candidatos/votacao-presidente`, {
                params: {
                    naoIniciadaPage: paginaNaoIniciada,
                    naoIniciadaSize: pageSize,
                    fechadaPage: paginaFechada,
                    fechadaSize: pageSize
                }
            });
            
            setDados(response.data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao carregar dados');
        } finally {
            setLoading(false);
            setIsAtualizando(false);
        }
    };

    useEffect(() => {
        carregarDados();
    }, [bancaId, paginaNaoIniciada, paginaFechada, isTutorialMode]);

    useEffect(() => {
        if (!isTutorialMode) {
            const interval = setInterval(() => {
                carregarDados(true);
            }, 20000);

            return () => clearInterval(interval);
        }
    }, [bancaId, paginaNaoIniciada, paginaFechada, isTutorialMode]);

    const verificarFotoRecursalDisponivel = async (candidatoId?: string) => {
        if (!bancaId || !candidatoId || !isBancaRecursal || isTutorialMode) {
            setFotoDisponivelRecursal(false);
            return;
        }

        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/${candidatoId}/fotoUrl`);
            setFotoDisponivelRecursal(Boolean(response.data?.url));
        } catch {
            setFotoDisponivelRecursal(false);
        }
    };

    useEffect(() => {
        void verificarFotoRecursalDisponivel(dados?.aberta?.candidato.id);
    }, [bancaId, isBancaRecursal, isTutorialMode, dados?.aberta?.candidato.id]);

    const formatarDataHora = (dataHora: string) => {
        return new Date(dataHora).toLocaleString('pt-BR');
    };

    const mapearVoto = (voto: string) => {
        switch (voto) {
            case 'VALIDADO':
                return 'Validado';
            case 'INCONCLUSIVO':
                return 'Inconclusivo';
            case 'NAO_VALIDADO':
                return 'Não Validado';
            default:
                return voto;
        }
    };

    const handleSolicitarInicioVotacao = async (candidatoId: string, candidatoNome: string) => {
        const candidato = { id: candidatoId, nome: candidatoNome };
        setCandidatoParaIniciar(candidato);
        if (configBanca.requerConfirmacaoInicio) {
            setShowConfirmarInicioModal(true);
        } else {
            handleIniciarVotacao(candidato);
        }
    };

    const handleIniciarVotacao = async (candidato?: { id: string; nome: string }) => {
        const candidatoParaUsar = candidato || candidatoParaIniciar;
        if (!candidatoParaUsar) return;
        
        setIsSubmitting(true);
        try {
            await VotacaoViewModel.patchAbrirVotacao(bancaId!, candidatoParaUsar.id);
            toast.success('Heteroidentificação iniciada.');
            carregarDados();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setIsSubmitting(false);
            setCandidatoParaIniciar(null);
        }
    };

    const handleFinalizarVotacao = async (candidatoId: string) => {
        try {
            await VotacaoViewModel.patchFecharVotacao(bancaId!, candidatoId);
            toast.success('Heteroidentificação encerrada.');
            carregarDados();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleMarcarComoAusente = (candidatoId: string, candidatoNome: string) => {
        setCandidatoSelecionado({ id: candidatoId, nome: candidatoNome });
        setShowConfirmarAusenciaModal(true);
    };

    const handleConfirmarAusencia = async () => {
        if (candidatoSelecionado) {
            try {
                await CandidatoViewModel.marcarCandidatoComoAusente(bancaId!, candidatoSelecionado.id);
                toast.success(`Ausência do candidato ${candidatoSelecionado.nome} registrada`);
                setShowConfirmarAusenciaModal(false);
                carregarDados();
            } catch (error) {
                toast.error(BackendErrorMessage(error));
            }
        }
    };

    const handleVotar = () => {
        setShowVotacaoModal(true);
    };

    const handleConfirmarVoto = async (resultado: VotoCandidato, justificativa: string) => {
        const candidatoId = dados?.aberta?.candidato.id;
        if (!candidatoId) {
            toast.error('Não foi possível identificar o candidato em votação.');
            return;
        }

        const votante: MembroBanca = {
            id: '',
            papelMembro: { id: '', nome: PapelNome.TITULAR },
            statusMembro: StatusMembroNome.DEFINIDO,
            pessoa: {
                id: 'x',
                vinculo: { id: 'x', nome: VinculoNome.DOCENTE },
                nome: 'x',
                email: 'x',
                cpf: 'x',
                isMembroCavu: true //Estranho
            }
        };

        const voto: Voto = {
            votante,
            resultado,
            justificativa,
            data: new Date(),
        };

        setIsVotando(true);
        try {
            await VotacaoViewModel.postVoto(bancaId!, candidatoId, voto);
            toast.success('Voto registrado');
            carregarDados();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setIsVotando(false);
            setShowVotacaoModal(false);
        }
    };

    const handleViewMidia = async (
        candidato: CandidatoBancaBasico,
        customMidia?: {
            endpoint: (bancaId: string, candidatoId: string) => string;
            tipo: 'video' | 'image';
            label: string;
        }
    ) => {
        if (!bancaId || !candidato.id) return;
        const endpointMidia = customMidia?.endpoint || configBanca.endpointMidia;
        if (!endpointMidia) return;
        const tipoMidiaSelecionada = customMidia?.tipo || configBanca.tipoMidia || 'video';
        const labelMidiaSelecionada = customMidia?.label || configBanca.labelMidia || 'Mídia';
        
        try {
            const endpoint = endpointMidia(bancaId, candidato.id);
            const response = await api.get(endpoint);
            if (response.data.url) {
                setMidiaUrl(response.data.url);
                setTipoMidia(tipoMidiaSelecionada);
                setTituloMidiaModal(`${labelMidiaSelecionada} do Candidato`);
                setShowMidiaModal(true);
            } else {
                toast.warning(`${labelMidiaSelecionada} não disponível para este candidato`);
            }
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleCloseMidiaModal = () => {
        setShowMidiaModal(false);
        if (midiaUrl) {
            URL.revokeObjectURL(midiaUrl);
            setMidiaUrl(null);
        }
    };

    const handleFinalizarBanca = () => {
        setShowFinalizarBancaModal(true);
    };

    const handleStartTutorial = () => {
        setIsStartingTutorial(true);
        
        // Iniciar tutorial na metade da animação (1 segundo)
        setTimeout(() => {
            setIsTutorialMode(true);
        }, 800);
        
        // Iniciar fade out
        setTimeout(() => {
            setIsFadingOut(true);
        }, 1000);
        
        // Finalizar transição após fade out
        setTimeout(() => {
            setIsStartingTutorial(false);
            setIsFadingOut(false);
        }, 1200);
    };

    const renderCandidato = (candidato: CandidatoBancaBasico, showActions = false) => {
        const opacity = isTutorialMode ? 
            (candidato.id === 'mock-1' ? tutorialOpacities.naoIniciado :
             candidato.id === 'mock-2' ? tutorialOpacities.andamento :
             candidato.id === 'mock-3' ? tutorialOpacities.finalizado : 1) : 1;
             
        return (
            <Card 
                className={`mb-2 ${isTutorialMode ? 'tutorial-candidato-mock' : ''}`} 
                key={candidato.inscricao}
                style={isTutorialMode ? { 
                    opacity, 
                    transition: 'opacity 1.8s ease-in-out' 
                } : {}}
            >
                <Card.Body className="py-2">
                    <Row>
                        <Col md={showActions ? 4 : (candidato.resultado ? 4 : 6)}>
                            <strong>{candidato.nomeSocial || candidato.nome}</strong>
                            <AutodeclaracaoRacialBadge autodeclaracaoRacial={candidato.autodeclaracaoRacial} />
                        </Col>
                        <Col md={showActions ? 4 : (candidato.resultado ? 4 : 6)} className={showActions ? "" : (candidato.resultado ? "" : "text-end")}>
                            <TooltipWrapper
                                tooltipContent={
                                    candidato.editais && candidato.editais.length > 0 ? (
                                        <div style={{ maxWidth: '300px' }}>
                                            {candidato.editais.map((edital, index) => (
                                                <div key={index} style={{ marginBottom: '4px' }}>
                                                    <strong>{edital.editalNome}</strong>
                                                    <br />
                                                    <span>Inscrição: {edital.inscricao}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : candidato.inscricao
                                }
                                tooltipPlacement="top"
                            >
                                <Badge bg="secondary" style={{ cursor: 'pointer' }}>
                                    Ver Edital/Inscrição
                                </Badge>
                            </TooltipWrapper>
                        </Col>
                        {candidato.resultado && (
                            <Col md={4} className="text-end">
                                <Badge 
                                    bg={
                                        candidato.resultado === 'VALIDADO'
                                            ? 'success'
                                            : candidato.resultado === 'AUSENTE'
                                                ? 'warning'
                                                : candidato.resultado === 'INCONCLUSIVO'
                                                    ? 'secondary'
                                                    : 'danger'
                                    }
                                    className="fs-6 px-3 py-2"
                                >
                                    {
                                        candidato.resultado === 'VALIDADO'
                                            ? 'Validado'
                                            : candidato.resultado === 'AUSENTE'
                                                ? 'Ausente'
                                                : candidato.resultado === 'INCONCLUSIVO'
                                                    ? 'Inconclusivo'
                                                    : 'Não Validado'
                                    }
                                </Badge>
                            </Col>
                        )}
                        {showActions && (
                            <Col md={4} className="text-end">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className={`me-2 shadow-sm ${!dados?.aberta ? 'btn-iniciar-destacado' : ''} ${isTutorialMode && candidato.id === 'mock-1' ? 'tutorial-btn-iniciar' : ''} ${highlightedButton === 'tutorial-btn-iniciar' && candidato.id === 'mock-1' ? 'btn-tutorial-highlight with-backdrop' : ''}`}
                                    onClick={isTutorialMode ? () => setShowTutorialModal(true) : () => candidato.id && handleSolicitarInicioVotacao(candidato.id, candidato.nome)}
                                    disabled={false}
                                    style={{ borderRadius: '6px', fontWeight: '600', backgroundColor: '#2c3e50', borderColor: '#2c3e50' }}
                                >
                                    Iniciar
                                </Button>
                                {(isTutorialMode || configBanca.temBotaoAusente) && (
                                    <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={isTutorialMode ? () => {} : () => candidato.id && handleMarcarComoAusente(candidato.id, candidato.nome)}
                                        disabled={false}
                                        className={`me-1 shadow-sm ${isTutorialMode && candidato.id === 'mock-1' ? 'tutorial-btn-ausente' : ''} ${highlightedButton === 'tutorial-btn-ausente' && candidato.id === 'mock-1' ? 'btn-tutorial-highlight with-backdrop' : ''}`}
                                        style={{ borderRadius: '6px', fontWeight: '600' }}
                                    >
                                        Ausente
                                    </Button>
                                )}

                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container className="py-5" style={{ maxWidth: '1200px' }}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <Spinner animation="border" role="status" style={{ color: '#2c3e50' }}>
                            <span className="visually-hidden">Carregando...</span>
                        </Spinner>
                        <p className="text-muted mt-3">Carregando dados da heteroidentificação...</p>
                    </div>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5" style={{ maxWidth: '1200px' }}>
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-5 text-center">
                        <Alert variant="danger" className="border-0">
                            <Alert.Heading className="fw-light">Erro ao carregar dados</Alert.Heading>
                            <p className="mb-0">{error}</p>
                        </Alert>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!dados) return null;

    return (
        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ABRIR_VOTACAO]} bancaIdFromComponent={bancaId!}>
            <Container className="py-5" style={{ maxWidth: '1200px' }}>
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <div>
                        <h2 className="mb-0 fw-light" style={{ color: '#2c3e50' }}>Painel do Presidente</h2>
                        <p className="text-muted mb-0">Gerenciamento da heteroidentificação</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant={showStatistics ? "primary" : "outline-secondary"}
                            size="sm"
                            onClick={() => setShowStatistics(!showStatistics)}
                            className="border-0 shadow-sm"
                            style={{ borderRadius: '8px' }}
                        >
                            {showStatistics ? <BsBarChartFill/> : <BsBarChart/>}
                        </Button>
                        <Button
                            variant={isTutorialMode ? "success" : "outline-primary"}
                            size="sm"
                            onClick={() => isTutorialMode ? resetTutorialState() : handleStartTutorial()}
                            disabled={isStartingTutorial}
                            className="border-0 shadow-sm"
                            style={{ borderRadius: '8px' }}
                        >
                            {isTutorialMode ? "Sair do Tutorial" : <BsFillQuestionCircleFill/>}
                        </Button>
                    </div>
                </div>
                {isAtualizando && (
                    <div className="text-muted small d-flex align-items-center justify-content-center mb-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Atualizando...
                    </div>
                )}
                {/* Estatísticas - Colapsável */}
                {showStatistics && (
                    <div className="mb-5">
                        <h3 className="fw-light text-center mb-4" style={{ color: '#2c3e50' }}>Estatísticas Gerais</h3>
                        <Row className="g-4">
                            <Col md={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-4 text-center">
                                        <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                            <BsBarChart className="text-warning" size={24} />
                                        </div>
                                        <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{dados.estatisticas.naoIniciados}</h2>
                                        <p className="text-muted mb-0 fw-semibold">Não Iniciados</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-4 text-center">
                                        <div className="bg-secondary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                            <BsCheckCircle className="text-secondary" size={24} />
                                        </div>
                                        <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{dados.estatisticas.fechados}</h2>
                                        <p className="text-muted mb-0 fw-semibold">Concluídos</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-4 text-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                            <BsCheckCircle className="text-success" size={24} />
                                        </div>
                                        <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{dados.estatisticas.validados}</h2>
                                        <p className="text-muted mb-0 fw-semibold">Validados</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6} lg={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-4 text-center">
                                        <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                                            <BsPeople className="text-danger" size={24} />
                                        </div>
                                        <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{dados.estatisticas.naoValidados}</h2>
                                        <p className="text-muted mb-0 fw-semibold">Não Validados</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                <Row>
                    {/* Candidato em Votação Aberta */}
                    <Col md={12} className="mb-4">
                        <Card className={`border-0 shadow-sm ${isTutorialMode ? "tutorial-andamento" : ""}`}>
                            <Card.Header className="border-0" style={{ backgroundColor: '#2c3e50', color: 'white', borderRadius: '8px 8px 0 0' }}>
                                <h5 className="mb-0 fw-light">Heteroidentificação em Andamento</h5>
                            </Card.Header>
                            <Card.Body>
                                {dados.aberta ? (
                                    <>
                                        <Row>
                                            <Col md={8}>
                                                <Card 
                                                    className={`mb-2 ${isTutorialMode ? 'tutorial-candidato-mock' : ''}`}
                                                    style={isTutorialMode ? { 
                                                        opacity: tutorialOpacities.andamento, 
                                                        transition: 'opacity 1.8s ease-in-out' 
                                                    } : {}}
                                                >
                                                    <Card.Body className="py-3">
                                                        <Row>
                                                            <Col md={8}>
                                                                <h3 className="mb-1">
                                                                    {dados.aberta.candidato.nomeSocial || dados.aberta.candidato.nome}
                                                                    <AutodeclaracaoRacialBadge autodeclaracaoRacial={dados.aberta.candidato.autodeclaracaoRacial} />
                                                                </h3>
                                                            </Col>
                                                            <Col md={4} className="text-end">
                                                                <TooltipWrapper
                                                                    tooltipContent={
                                                                        dados.aberta.candidato.editais && dados.aberta.candidato.editais.length > 0 ? (
                                                                            <div style={{ maxWidth: '300px' }}>
                                                                                {dados.aberta.candidato.editais.map((edital, index) => (
                                                                                    <div key={index} style={{ marginBottom: '4px' }}>
                                                                                        <strong>{edital.editalNome}</strong>
                                                                                        <br />
                                                                                        <span>Inscrição: {edital.inscricao}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : dados.aberta.candidato.inscricao
                                                                    }
                                                                    tooltipPlacement="top"
                                                                >
                                                                    <Badge bg="secondary" style={{ cursor: 'pointer' }}>
                                                                        Ver Inscrição
                                                                    </Badge>
                                                                </TooltipWrapper>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={4} className="text-center">
                                                <div className="tutorial-voting-section">
                                                    <h4 className="text-primary">{isTutorialMode ? tutorialVotosRealizados : dados.estatisticas.votosRealizados}</h4>
                                                    <small>{(isTutorialMode ? tutorialVotosRealizados : dados.estatisticas.votosRealizados) == 1 ? "Já votou" : "Já votaram"}</small>
                                                    <div className="mt-3">
                                                        {(isTutorialMode || configBanca.temBotaoMidia) && (
                                                            <Button
                                                                variant="dark"
                                                                onClick={isTutorialMode ? () => {} : () => handleViewMidia(dados.aberta!.candidato)}
                                                                className="me-2"
                                                                disabled={false}
                                                            >
                                                                {configBanca.iconeMidia && React.createElement(configBanca.iconeMidia as React.ComponentType<any>, { className: 'me-1' })}
                                                                {configBanca.labelMidia || 'Mídia'}
                                                            </Button>
                                                        )}
                                                        {isBancaRecursal && fotoDisponivelRecursal && (
                                                            <Button
                                                                variant="outline-dark"
                                                                onClick={isTutorialMode ? () => {} : () =>
                                                                    handleViewMidia(dados.aberta!.candidato, {
                                                                        endpoint: (idBanca, idCandidato) =>
                                                                            `/bancas/${idBanca}/candidatos/${idCandidato}/fotoUrl`,
                                                                        tipo: 'image',
                                                                        label: 'Foto'
                                                                    })
                                                                }
                                                                className="me-2"
                                                                disabled={false}
                                                            >
                                                                Foto
                                                            </Button>
                                                        )}
                                                        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.VOTAR_CANDIDATO]} bancaIdFromComponent={bancaId!}>
                                                            <Button
                                                                variant="success"
                                                                onClick={isTutorialMode ? () => {} : handleVotar}
                                                                disabled={false}
                                                                className={`${!dados.voto ? 'btn-votar-destacado' : ''} shadow-sm`}
                                                                style={{ borderRadius: '8px', fontWeight: '600' }}
                                                            >
                                                                {isTutorialMode ? 'Votar' : (dados.voto ? 'Alterar Voto' : 'Votar')}
                                                            </Button>
                                                        </PermissaoWrapper>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                        {dados.voto && (
                                            <Alert 
                                                variant={dados.voto.voto == "VALIDADO" ? "success" : "danger"} 
                                                className="mt-3"
                                                style={isTutorialMode ? { 
                                                    opacity: tutorialOpacities.andamento, 
                                                    transition: 'opacity 1.8s ease-in-out' 
                                                } : {}}
                                            >
                                                <div><strong>Seu voto:</strong> {mapearVoto(dados.voto.voto)}</div>
                                                {dados.voto.justificativa && (
                                                    <div><strong>Justificativa:</strong> {dados.voto.justificativa}</div>
                                                )}
                                                <small className="text-muted">
                                                    Votado em: {formatarDataHora(dados.voto.dataHoraVotacao)}
                                                </small>
                                            </Alert>
                                        )}
                                        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.FECHAR_VOTACAO]} bancaIdFromComponent={bancaId!}>
                                            <div className="text-end mt-3">
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={isTutorialMode ? () => {} : () => handleFinalizarVotacao(dados.aberta!.candidato.id!)}
                                                    disabled={false}
                                                    className={`shadow-sm ${dados.estatisticas.votosRealizados >= 5 ? 'btn-finalizar-destacado' : ''}`}
                                                    style={{ 
                                                        borderRadius: '8px', 
                                                        fontWeight: '600',
                                                        color: '#58100bff',
                                                        borderColor: '#6c757d'
                                                    }}
                                                >
                                                    Finalizar Heteroidentificação do Candidato
                                                </Button>
                                            </div>
                                        </PermissaoWrapper>
                                    </>
                                ) : (
                                    <p className="text-muted">Nenhuma heteroidentificação em andamento no momento.</p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Candidatos Não Iniciados */}
                    <Col md={6} className="mb-4">
                        <Card className={`border-0 shadow-sm ${isTutorialMode ? "tutorial-nao-iniciados" : ""}`}>
                            <Card.Header className="border-0" style={{ backgroundColor: '#ffc107', color: '#212529', borderRadius: '8px 8px 0 0' }}>
                                <h5 className="mb-0 fw-light">Não Iniciados ({dados.naoIniciada.candidatos.totalElements})</h5>
                            </Card.Header>
                            <Card.Body>
                                {dados.naoIniciada.candidatos.content.length > 0 ? (
                                    <>
                                        {dados.naoIniciada.candidatos.content.map((candidato, index) => renderCandidato(candidato, true))}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <small className="text-muted">
                                                Página {(dados.naoIniciada.candidatos.pageable?.pageNumber ?? 0) + 1} de {dados.naoIniciada.candidatos.totalPages || 1}
                                            </small>
                                            <PaginationComponent
                                                currentPage={(dados.naoIniciada.candidatos.pageable?.pageNumber ?? 0) + 1}
                                                totalPages={dados.naoIniciada.candidatos.totalPages || 1}
                                                onPageChange={(page) => setPaginaNaoIniciada(page - 1)}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-muted">Nenhum candidato aguardando início.</p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Candidatos com Votação Fechada */}
                    <Col md={6} className="mb-4">
                        <Card className={`border-0 shadow-sm ${isTutorialMode ? "tutorial-finalizados" : ""}`}>
                            <Card.Header className="border-0" style={{ backgroundColor: '#6c757d', color: 'white', borderRadius: '8px 8px 0 0' }}>
                                <h5 className="mb-0 fw-light">Concluídos ({dados.fechada.candidatos.totalElements})</h5>
                            </Card.Header>
                            <Card.Body>
                                {dados.fechada.candidatos.content.length > 0 ? (
                                    <>
                                        {dados.fechada.candidatos.content.map((candidato, index) => renderCandidato(candidato, false))}
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <small className="text-muted">
                                                Página {(dados.fechada.candidatos.pageable?.pageNumber ?? 0) + 1} de {dados.fechada.candidatos.totalPages || 1}
                                            </small>
                                            <PaginationComponent
                                                currentPage={(dados.fechada.candidatos.pageable?.pageNumber ?? 0) + 1}
                                                totalPages={dados.fechada.candidatos.totalPages || 1}
                                                onPageChange={(page) => setPaginaFechada(page - 1)}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-muted">Nenhuma heteroidentificação concluída.</p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Botão Finalizar Banca */}
                <div className="text-center mt-5 pt-4">
                    <div className="mx-auto mb-4" style={{ width: '100px', height: '1px', backgroundColor: '#dee2e6' }}></div>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.FINALIZAR_BANCA]} bancaIdFromComponent={bancaId!}>
                        <Button 
                            variant={bancaFinalizada ? 'warning' : 'dark'} 
                            onClick={isTutorialMode ? () => {} : handleFinalizarBanca}
                            disabled={false}
                            size="lg"
                            className="shadow-sm px-5 py-3"
                            style={{ 
                                borderRadius: '8px', 
                                fontWeight: '600',
                                backgroundColor: bancaFinalizada ? '#ffc107' : '#2c3e50',
                                borderColor: bancaFinalizada ? '#ffc107' : '#2c3e50'
                            }}
                        >
                            <BsFillPatchCheckFill className="me-2" />
                            {bancaFinalizada ? 'BANCA FINALIZADA' : 'Finalizar Banca'}
                        </Button>
                    </PermissaoWrapper>
                </div>

                <ConfirmarAusenciaModal
                    show={showConfirmarAusenciaModal}
                    onHide={() => setShowConfirmarAusenciaModal(false)}
                    onConfirmar={handleConfirmarAusencia}
                    candidatoId={candidatoSelecionado?.id || ''}
                    candidatoNome={candidatoSelecionado?.nome || ''}
                />

                <VotacaoModal
                    show={showVotacaoModal}
                    onHide={() => setShowVotacaoModal(false)}
                    onConfirmar={handleConfirmarVoto}
                    votarEnabled={!isVotando}
                    setVotarEnabled={() => {}}
                    isFacial={tipoBanca === TipoBancaNome.FACIAL}
                />

                <MidiaModal
                    show={showMidiaModal}
                    onHide={handleCloseMidiaModal}
                    midiaUrl={midiaUrl}
                    tipo={tipoMidia}
                    titulo={tituloMidiaModal}
                />

                <FinalizarBancaModal
                    show={showFinalizarBancaModal}
                    onHide={() => setShowFinalizarBancaModal(false)}
                    bancaId={bancaId!}
                    bancaFinalizada={bancaFinalizada}
                    setBancaFinalizada={setBancaFinalizada}
                />

                <ConfirmarInicioModal
                    show={isTutorialMode ? showTutorialModal : showConfirmarInicioModal}
                    onHide={() => isTutorialMode ? setShowTutorialModal(false) : setShowConfirmarInicioModal(false)}
                    onConfirmar={isTutorialMode ? () => setShowTutorialModal(false) : handleIniciarVotacao}
                    candidatoNome={isTutorialMode ? 'João Silva Santos' : (candidatoParaIniciar?.nome || '')}
                />

                <TutorialVotacaoPresidente
                    isTutorialMode={isTutorialMode}
                    onTutorialComplete={resetTutorialState}
                    onOpacityChange={(naoIniciado, andamento, finalizado) => 
                        setTutorialOpacities({ naoIniciado, andamento, finalizado })
                    }
                    onShowModal={setShowTutorialModal}
                    onHighlightButton={setHighlightedButton}
                    onUpdateVoteCount={setTutorialVotosRealizados}
                />
                
                {/* Overlay de transição para iniciar tutorial */}
                {isStartingTutorial && (
                    <div className="tutorial-transition-overlay"></div>
                )}

                {/* CSS para highlight do tutorial */}
                {(isTutorialMode || isStartingTutorial) && (
                    <style>
                        {`
                            .tutorial-transition-overlay {
                                position: fixed;
                                top: 0;
                                left: 0;
                                width: 100vw;
                                height: 100vh;
                                background: rgba(255, 255, 255, 0.3);
                                backdrop-filter: blur(8px);
                                z-index: 9999;
                                animation: tutorial-fade-in 0.5s ease-in-out;
                            }
                            
                            @keyframes tutorial-fade-in {
                                from { opacity: 0; backdrop-filter: blur(0px); }
                                to { opacity: 1; backdrop-filter: blur(8px); }
                            }
                            
                            .btn-tutorial-highlight {
                                animation: tutorial-pulse 2s infinite;
                                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
                                position: relative;
                                z-index: 1001;
                            }
                            .btn-tutorial-highlight.with-backdrop::before {
                                content: '';
                                position: fixed;
                                top: 0;
                                left: 0;
                                width: 100vw;
                                height: 100vh;
                                background: rgba(255, 255, 255, 0.1);
                                backdrop-filter: blur(3px);
                                z-index: -1;
                                pointer-events: none;
                            }

                            @keyframes tutorial-pulse {
                                0% { box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5); }
                                50% { box-shadow: 0 0 0 0.4rem rgba(0, 123, 255, 0.3); }
                                100% { box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5); }
                            }
                        `}
                    </style>
                )}

            </Container>
        </PermissaoWrapper>
    );
};

export default VotacaoPresidente;
